/**
 * Client-journey eligibility — single source of truth.
 *
 * Two independent gates:
 *  1. JOURNEY_CLIENT_FACING_TYPE_IDS — the deliverable types a client actually receives or
 *     reviews as a journey moment. This drives whether posting a milestone offers "add to the
 *     Client Hub". Matched by the deliverable type's stable Airtable record id (2026-09-22 —
 *     was matched by Name, which breaks silently on a rename). Edit this set in one place to
 *     change which posts prompt.
 *  2. isMilestonePosted — a milestone may only be added to the journey once it has been POSTED
 *     (delivered). The Client Hub only ever shows delivered work.
 *  3. SHEET_GATE_MS_TYPE_IDS — milestone types whose deliverable IS a drawing set. These require
 *     at least one QC-approved sheet revision before they can be posted. They also render as an
 *     interactive DrawingSetViewer card on the Client Hub rather than a document list.
 *
 * NOTE on journey_entries.milestone_type: an already-published journey entry stores a frozen
 * NAME snapshot of the milestone's type at the moment it was posted (protects the historical
 * record from a later rename). `isClientFacingDeliverable`/`isSheetGateMilestone` below take an
 * ID and are for LIVE milestones only (posting-time checks, where the current type id is always
 * available) — matching against an old snapshot's frozen NAME is a separate, deliberately
 * name-based path (see journey/modules/MilestoneEntry.jsx and api/_shareDocs.js) and is out of
 * scope for this id-conversion pass; converting stored historical snapshots to ids would need a
 * backfill decision of its own.
 */

export const JOURNEY_CLIENT_FACING_TYPE_IDS = new Set([
  // Pre-design
  'recpwPFowkQYX60TE', // Pre-design Research
  'recJP315SFdjdm6Wd', // Program and Control Budget
  // Schematic Design
  'reco92NDyQJ20iaTa', // Conceptual Framework
  'recU1WO5xyleiBpQv', // Conceptual Framework Revisions
  'rec3krW4Ii3yZRoQc', // Integrated Design Proposal
  'recBlhLoj6qVAjArw', // IDP Revisions
  // Design Development
  'rec4FpOj0bTvR3fH9', // Architectural Selections Package & Outline Spec
  // Interior Architecture
  'recqeWggNMMOyoR4p', // Interior Concept Package
  'reca6Cv2JePucZ4Bp', // Interior Selections Package
  'recjwAzQxqThAEJyt', // Interior Design Refinement
  // Additional Services
  'rec5ZDopLm6O99s8r', // Rendering Package
  'recYJ26aU8cDuiTib', // Client Requested Design Revision - Drawings
  'recxNKoZtc9WlqCqF', // Client Requested Design Revision - Concept
  // Survey / Existing Conditions
  'recwkLhUGJl7d48hf', // Existing Building 3D Model
  // Permit drawing-production milestones (sheet-gate milestones — require QC-approved sheets before posting)
  'reccrHto8inB7Ycj2', // Permit Plan Check Drawings
  'recJRA75E5RKIeVBu', // Discretionary Approvals Plan Check Drawings
  'recHf9ezgYszxlPfJ', // PPC Drawings Plan Check Revision
  'recGcYRX5M5nr7xS9', // PPC Drawings Permit Revision
  'recjet03Rp2kKO3aq', // DAPC Drawings Revisions
  // 'Plan Check Resubmission 🔹' (recO3wapjkgPiP4C9) deliberately excluded (2026-08-31) — it's
  // the submission-to-agency step for a package already prepared/journeyed under PPC Drawings
  // Plan Check Revision, not a new client deliverable. Mirrors 'Permit Revision Submission 🔹'
  // (recKSL31A0pYA3rjX), its Permit-track sibling, which was never included here.
  // Specifications — render as portrait report viewer
  'recJv58DnUxXKEfo0', // Short Form Residential Specifications
  'recHXZnhdTpAg8ZUF', // Three Part Technical Specifications
])

/** Is this deliverable type id one a client would see on the journey? */
export function isClientFacingDeliverable(typeId) {
  return !!typeId && JOURNEY_CLIENT_FACING_TYPE_IDS.has(typeId)
}

/**
 * Milestone types whose primary deliverable is a drawing set. Two consequences:
 *  1. Posting is blocked unless at least one sheet revision exists and all are QC-approved.
 *  2. The journey card shows an interactive DrawingSetViewer instead of a document list.
 * Add to this set whenever a new drawing-release milestone type is introduced.
 */
export const SHEET_GATE_MS_TYPE_IDS = new Set([
  'recwkLhUGJl7d48hf', // Existing Building 3D Model
  'reccrHto8inB7Ycj2', // Permit Plan Check Drawings
  'recJRA75E5RKIeVBu', // Discretionary Approvals Plan Check Drawings
  // Revision milestones — delta drawing sets issued to the client.
  'recHf9ezgYszxlPfJ', // PPC Drawings Plan Check Revision
  'recGcYRX5M5nr7xS9', // PPC Drawings Permit Revision
  'recjet03Rp2kKO3aq', // DAPC Drawings Revisions
  // NOTE: 'Plan Check Resubmission 🔹' (recO3wapjkgPiP4C9) is deliberately excluded
  // (2026-08-31) — it's the submission-to-agency step for a package already prepared under PPC
  // Drawings Plan Check Revision, not a drawing-production step. Its sheets were already added +
  // QC'd there; gating this milestone too just blocked posting the resubmission itself. Mirrors
  // 'Permit Revision Submission 🔹' (recKSL31A0pYA3rjX), its Permit-track sibling, never gated.
])

/** Is this milestone type id gated on having QC-approved drawing sheets? */
export function isSheetGateMilestone(typeId) {
  return !!typeId && SHEET_GATE_MS_TYPE_IDS.has(typeId)
}

const getFirst = v => (Array.isArray(v) ? v[0] : v)

/** A milestone is journey-eligible only once posted (delivered to the client). Accepts an Airtable
 *  record ({ fields }) or a bare fields object. Posted = a truthy 'Posted Date'. */
export function isMilestonePosted(ms) {
  const f = (ms && ms.fields) || ms || {}
  return !!getFirst(f['Posted Date'])
}
